import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import { UPDATE_ORDER } from '@/store/modules/order.module'
import {
  apiToastError,
  apiToastErrorV2,
  apiToastSuccess,
} from '@/@core/utils/toast'
import { formatNumber } from '@/@core/utils/utils'
import { MONTH_START_DATE } from '@/constants'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'

const {
  calculateCommercialTerms,
  convertValueToPercent,
  formatCurrency,
  getMonthAndYearFromDate,
} = utils

const {
  KP_MONTH_FORMAT,
  ORDER_STATUS,
} = c

function hasAnyProductDiscount(products) {
  if (!products) return false
  return products.some(product => product.discount !== undefined && product.discount !== null && product.discount < 1)
}

export function useOrderSummary() {
  const requestDeliveryDate = ref(null)
  const changeDate = ref(false)
  const onDisplayCalender = ref(false)
  const editRequestDeliveryDate = ref(false)
  const requestedDeliveryDate = ref(null)
  const expectedAvailabilityDate = ref(null)
  const discount = ref(null)
  const canEditDate = ref(true)
  const isEditDiscount = ref(false)

  const isBrand = computed(() => store.getters.isBrand)
  const isRetailer = computed(() => store.getters.isRetailer)

  function currency(order) {
    if (isBrand.value) {
      return order.currencyForBrand
    }
    return order.currency
  }

  function shouldDisplayCreditPoints(order) {
    return (
      isRetailer.value
      && ((order.discountInCredit && !order.isDataForCreateShipment)
        || (order.discountInCredit
          && order.isDataForCreateShipment
          && order.remainingCredits))
    )
  }

  function isDiscountVisible(order) {
    return !order.shipmentId && order.isSelfManagedOrder && isBrand.value
  }

  function initRequestedDeliveryDates(order) {
    if (order.requestedDeliveryDate) {
      requestDeliveryDate.value = getMonthAndYearFromDate(
        order.requestedDeliveryDate,
      )
      requestedDeliveryDate.value = getMonthAndYearFromDate(
        order.requestedDeliveryDate,
      )
    }
  }

  function initOrderSummary(order) {
    initRequestedDeliveryDates(order)
    expectedAvailabilityDate.value = order.expectedAvailabilityDate || null
    discount.value = convertValueToPercent(order?.discount || 0)
    canEditDate.value = !(
      ORDER_STATUS.CANCELLED === order.status
      || ORDER_STATUS.REJECTED === order.status
    )
  }

  function onUpdateAvailabilityDate(orderId) {
    const date = `${MONTH_START_DATE}${requestDeliveryDate.value}`
    const payload = {
      requestedDeliveryDate: date,
    }
    return store
      .dispatch(UPDATE_ORDER, {
        orderId,
        payload,
      })
      .then(res => {
        changeDate.value = false
        onDisplayCalender.value = false
        editRequestDeliveryDate.value = false
        requestedDeliveryDate.value = requestDeliveryDate.value
        apiToastSuccess(res.data.message || 'Updated date!')
      })
      .catch(() => { })
  }

  function onUpdateExpectedAvailabilityDate(date, orderId) {
    if (isRetailer.value) {
      return
    }
    // Convert the date to MM/YYYY format for display
    const selectedDate = date[0]
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
    const year = selectedDate.getFullYear()
    const displayDate = `${month}/${year}`

    // Backend expects "01/MM/YYYY" format (full date with day as 01)
    const formattedDate = `${MONTH_START_DATE}${month}/${year}`

    const payload = {
      expectedAvailabilityDate: formattedDate,
    }
    return store
      .dispatch(UPDATE_ORDER, { orderId, payload })
      .then(() => {
        apiToastSuccess('Estimated delivery window updated successfully')
        expectedAvailabilityDate.value = displayDate
        onDisplayCalender.value = false
        return { success: true, displayDate }
      })
      .catch(error => {
        apiToastError(error)
        onDisplayCalender.value = false
        return { success: false }
      })
  }

  function onUpdateDiscount(orderId) {
    const payload = {
      discount: calculateCommercialTerms(Number(discount.value)),
    }
    return store
      .dispatch(UPDATE_ORDER, { orderId, payload })
      .then(() => {
        isEditDiscount.value = false
        apiToastSuccess('Discount updated successfully')
        return { success: true }
      })
      .catch(error => {
        apiToastErrorV2(error)
        return { success: false }
      })
  }

  function onRequestedDeliveryDateChange(selectedDate) {
    changeDate.value = false
    if (selectedDate && selectedDate !== requestedDeliveryDate.value) {
      changeDate.value = true
    }
  }

  function resetDate(originalDate) {
    changeDate.value = false
    onDisplayCalender.value = false
    editRequestDeliveryDate.value = false
    requestDeliveryDate.value = originalDate
  }

  return {
    // State
    requestDeliveryDate,
    changeDate,
    onDisplayCalender,
    editRequestDeliveryDate,
    requestedDeliveryDate,
    expectedAvailabilityDate,
    discount,
    canEditDate,
    isEditDiscount,

    // Computeds
    isBrand,
    isRetailer,

    // Methods
    currency,
    shouldDisplayCreditPoints,
    isDiscountVisible,
    initOrderSummary,
    initRequestedDeliveryDates,
    onUpdateAvailabilityDate,
    onUpdateExpectedAvailabilityDate,
    hasAnyProductDiscount,
    onUpdateDiscount,
    onRequestedDeliveryDateChange,
    resetDate,

    // Utilities
    formatNumber,
    formatCurrency,
    KP_MONTH_FORMAT,
    getMonthAndYearFromDate,
  }
}
